import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import {
  getNotification,
  getProviders,
  getOperationProvidersOyM,
  getAlert,
  getNotificationOyM,
  getEymExpiredContracts,
  getOymExpiredContracts,
  getOperationContractsOyM,
  getContractToASDS
} from '../../services/adminContracts';
import { getNotificationASDS } from '../../services/automaticServiceRequest';
import { getAllBusinessUnits, getAllOperations } from '../../services/yclickMaster';
import useToast from '../../hooks/useToast';
import { ubicationTypeEnum } from '../../utils/dataStatus';

const useProviderForm = (props) => {
  const {
    providerValue,
    providerField,
    setNotificationArray,
    requestValue,
    requestDetails = false,
    createASDS = false,
    isAutocomplete = false
  } = props;
  const [businessUnitSwitch, setBusinessUnitSwitch] = useState(false);
  const [businessUnit, setBusinessUnitList] = useState([]);
  const [contractArray, setContractArray] = useState([]);
  const { setNewToast } = useToast();

  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const switchBusinessUnit = () => setBusinessUnitSwitch((oldValue) => !oldValue);

  const fetchBusinessUnits = async () => {
    try {
      const businessUnits = values.isOyM ? await getAllOperations() : await getAllBusinessUnits();

      setBusinessUnitList(businessUnits);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const fetchProviders = async (value, idBusinessUnit, idService) => {
    if (value.length < 3) return null;
    const providers = values.isOyM
      ? await getOperationProvidersOyM({
          operacionId: idBusinessUnit,
          servicioId: idService,
          proveedor: value
        })
      : await getProviders(idBusinessUnit, idService, value);

    const currentProvidersIds = new Set(
      providerValue.reduce((acc, provider) => {
        if (provider?.provider?.id) acc.push(provider.provider.id);

        return acc;
      }, [])
    );

    return providers?.data?.proveedores?.filter(
      (provider) => !currentProvidersIds.has(provider.id)
    );
  };

  const createArrayOfContracts = ({ contracts, providerIndex }) => {
    const initalContracts = contracts.map((contract) => {
      const contractOption = contract.contratoVigente
        ? contract?.nombre
        : `${contract?.nombre} (vencido)`;

      return {
        ...contract,
        label: contractOption,
        nombre: contract?.nombre
      };
    });
    const initialContractMap = new Map(contractArray.map((contract, index) => [index, contract]));

    initialContractMap.set(providerIndex, {
      contracts: initalContracts
    });

    return Array.from(initialContractMap, ([_, value]) => value);
  };

  const mapContracts = ({ contracts, providerIndex, idContract }) => {
    const mappedContracts = contracts.length
      ? createArrayOfContracts({
          contracts,
          providerIndex
        })
      : contracts;

    setContractArray(mappedContracts);

    // Al replicar la informacion de una solicitud, trae en la consulta de los proveedores info inconsistente.
    // Los campos que son inconsistentes son: requiereAceptacion y requiereAdjuntarArchivo.
    // Por ese motivo seteamos nuevamente los datos desde la consulta a contratos en formik
    contracts.forEach((res) => {
      const contractOption = res.contratoVigente ? res?.nombre : `${res?.nombre} (vencido)`;

      if (res?.id === idContract) {
        setFieldValue(`${providerField}[${providerIndex}].contract`, {
          id: res?.id,
          label: contractOption,
          nombre: res?.nombre,
          requiereAceptacion: res?.requiereAceptacion,
          requiereAdjuntarArchivo: res?.requiereAdjuntarArchivo,
          value: res?.id
        });
      }
    });
    if (contracts?.every((contract) => contract?.id !== idContract)) {
      setFieldValue(`${providerField}[${providerIndex}].contract`, null);
    }
  };

  const getEventId = (val, idEvento) => {
    const ubication = val?.ubication;
    const ubicationTypeId = ubication?.ubicationType?.id;
    const isWell = ubicationTypeEnum.pozo === ubicationTypeId;
    const isLocation = ubicationTypeEnum.locacion === ubicationTypeId;

    if (idEvento) {
      return idEvento;
    }

    if (isLocation) {
      return ubication?.typeEvent?.id;
    }

    return isWell && ubication?.event?.id === 0
      ? ubication?.operationType?.id
      : ubication?.event?.id;
  };

  const fetchContract = async (
    idBusinessUnit,
    idService,
    Providerid,
    providerIndex,
    idContract,
    idEvento = null
  ) => {
    try {
      let response;

      if (createASDS) {
        response = values.isOyM
          ? await getOperationContractsOyM({
              proveedorId: Providerid,
              servicioId: idService,
              operacionId: idBusinessUnit,
              requiereSolicitud: true
            })
          : await getContractToASDS({
              proveedorId: Providerid,
              unidadNegocioId: idBusinessUnit,
              servicioId: idService,
              eventoId: values.ubication.event.id,
              requiereSolicitud: createASDS ? false : null
            });
      } else {
        response = values.isOyM
          ? await getOymExpiredContracts({
              proveedorId: Providerid,
              servicioId: idService,
              operacionId: idBusinessUnit,
              requiereSolicitud: true
            })
          : await getEymExpiredContracts({
              idBusinessUnit,
              idService,
              Providerid,
              requireRequest: createASDS ? false : null,
              eventoId: getEventId(values, idEvento)
            });
      }
      let contractsToMap = [];

      if (createASDS) {
        contractsToMap = response?.data?.contratos;
      } else {
        contractsToMap = response?.data;
      }
      mapContracts({
        contracts: contractsToMap,
        idContract,
        providerIndex
      });
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const removeContractsFromProvider = (index) => {
    setContractArray((state) => state.filter((_, idx) => idx !== index));
  };

  const fetchAlerts = async (nroContract, index) => {
    try {
      const [contractWithoutExpired] = nroContract.split(' ', 2);
      const response = await getAlert(contractWithoutExpired);

      const { data } = response;

      setFieldValue(`${providerField}[${index}].alert`, {
        alert: data.alerta,
        message: data.mensajes,
        availableAmount: data.montoDisponible
      });
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  const fetchNotification = async (idBusinessUnit, contractNumber, index) => {
    try {
      let notification;

      if (createASDS) {
        notification = await getNotificationASDS();
        setFieldValue(`${providerField}[${index}].notificate`, {
          ...notification.default,
          label: notification.default.nombre,
          value: notification.default.id
        });
        setNotificationArray(notification.opciones);
      } else {
        const [contractWithoutExpired] = contractNumber.split(' ', 2);

        notification = values.isOyM
          ? await getNotificationOyM({
              nroContrato: contractWithoutExpired,
              idOperacion: idBusinessUnit
            })
          : await getNotification(idBusinessUnit, contractWithoutExpired);

        setFieldValue(`${providerField}[${index}].notificate`, {
          ...notification.data.default,
          label: notification.data.default.nombre,
          value: notification.data.default.id
        });
        setNotificationArray(notification.data.opciones);
      }

      setFieldTouched(`${providerField}[${index}].notificate`);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const switchInputChange = () => {
    switchBusinessUnit();
    const filteredProviders = providerValue?.filter(
      (p) => p.businessUnit?.id === values.ubication?.uunn?.id
    );

    if (filteredProviders.length) {
      setFieldValue(`${providerField}`, filteredProviders);
    } else if (createASDS) {
      setFieldValue(`${providerField}`, [
        {
          contract: null,
          notificate: null,
          provider: providerValue[0]?.provider,
          businessUnit: {
            id: values.ubication.uunn.id,
            value: values.ubication.uunn.id,
            nombre: values.ubication.uunn.nombre,
            label: values.ubication.uunn.nombre
          }
        }
      ]);
    } else {
      setFieldValue(`${providerField}`, [
        {
          contract: null,
          notificate: null,
          provider: null,
          businessUnit: {
            id: values.ubication.uunn.id,
            value: values.ubication.uunn.id,
            nombre: values.ubication.uunn.nombre,
            label: values.ubication.uunn.nombre
          }
        }
      ]);
    }
  };

  useEffect(() => {
    fetchBusinessUnits();
    if (!requestDetails)
      setFieldValue(`${providerField}[${0}].businessUnit`, values.ubication.uunn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line array-callback-return
    if (isAutocomplete) {
      providerValue?.forEach((provider, index) => {
        if (provider?.provider?.id && provider?.businessUnit?.id) {
          fetchContract(
            provider?.businessUnit?.id,
            requestValue?.service?.service?.id,
            provider?.provider?.id,
            index,
            provider?.contract?.id
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    businessUnit,
    businessUnitSwitch,
    contractArray,
    setBusinessUnitList,
    setBusinessUnitSwitch,
    setContractArray,
    fetchBusinessUnits,
    fetchProviders,
    fetchContract,
    removeContractsFromProvider,
    fetchNotification,
    switchInputChange,
    switchBusinessUnit,
    fetchAlerts
  };
};

export default useProviderForm;
